<script setup lang="ts">
    import { formatAmount, formatDate } from '@/utils';
    import { useRoute } from 'vue-router';
    import { inject, ref, computed, onMounted } from 'vue';
    import axios from 'axios';
    import moment from 'moment';

    const route = useRoute();
    const BACKEND_URL = inject('BACKEND_URL');
    const user: any = ref(route.meta.user || {});
    const loading = ref(false);
    const errorMessage = ref('');

    onMounted(async () => {
        // Recharger les données utilisateur pour s'assurer qu'elles sont à jour
        if (route.params.id) {
            try {
                const response = await axios.get(`/backoffice/users/get/${route.params.id}`);
                user.value = response.data;
            } catch (error: any) {
                console.error('Erreur lors du chargement de l\'utilisateur:', error);
            }
        }
    });

    function formatDateFrench(dateString: string | null): string {
        if (!dateString) return '';
        try {
            const date = moment(dateString);
            if (!date.isValid()) return '';
            const monthsFr = ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
            return `${date.date()} ${monthsFr[date.month()]} ${date.year()}`;
        } catch {
            return dateString;
        }
    }

    function calculateAnciennete(dateEngagement: string | null): string {
        if (!dateEngagement) return '-';
        try {
            const engagementDate = moment(dateEngagement);
            if (!engagementDate.isValid()) return '-';
            const now = moment();
            const years = now.diff(engagementDate, 'years');
            const months = now.diff(engagementDate, 'months') % 12;
            
            if (years > 0 && months > 0) {
                return `${years} an${years > 1 ? 's' : ''} ${months} mois`;
            } else if (years > 0) {
                return `${years} an${years > 1 ? 's' : ''}`;
            } else if (months > 0) {
                return `${months} mois`;
            } else {
                return '0 mois';
            }
        } catch {
            return '-';
        }
    }

    const quotiteUsedPercent = computed(() => {
        if (!user.value.quotite_cessible_montant || !user.value.salaire || user.value.salaire <= 0) return 0;
        // Si on a une utilisation, on peut la calculer, sinon 0
        // Pour l'instant, on retourne 0 car on n'a pas de données d'utilisation
        return 0;
    });

    async function doSendConfirmationEmail() {
        loading.value = true;
        errorMessage.value = '';
        try {
            await axios.post(`/backoffice/users/send_confirmation_email`, { users_ids: [user.value.id] });
        } catch (error: any) {
            errorMessage.value = error.response?.data?.message || 'Une erreur est survenue lors de l\'envoi de l\'email.';
        } finally {
            loading.value = false;
        }
    }
</script>
<template>
    <section class="container-full-1 bg-grey-50">
        <div class="page-space-40"></div>

        <h4 class="s7 font-semibold px-3 pb-5">Membre / détail <span class="text-sm text-grey-500"></span></h4>
        <div class="s13 flex-right gap-3 mt--2 mb-5 px-5">
            <span class="btn shadow-200 white2">Souscription</span>
            <span class="btn shadow-200 white2">Contrat</span>
            <span class="btn shadow-200 light2 border-none">Prélèvement</span>
            <span class="btn shadow-200 light2 border-none">Premier prélèvement</span>
            <span class="btn shadow-200 light2 border-none">Décaissement mensuel</span>
            <span class="btn shadow-200 light2 border-none">Wallet / Utilisation</span>
        </div>

        <!--Souscription - Component-->
        <div class="s20 widget bg-gradient-dark px-6 pt-8 pb-1">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Souscription
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn-ultra color2">
                    <div class="btn-show">
                        <img src="/icons/icon-actif.svg" class="btn-icon" alt="" />
                        <span>Actif</span>
                    </div>
                    <div class="btn-loading">
                        <div class="loader-dots"></div>
                    </div>
                </button>
                <button class="btn-ultra"><!--loading-->
                    <div class="btn-show">
                        <img src="/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
                        <span>Valider</span>
                    </div>
                    <div class="btn-loading">
                        <div class="loader-dots"></div>
                    </div>
                </button>
                <button class="btn lg py-3 white2">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Utilisation</span>
                </button>
            </div>
            <div class="clear-both h-10"></div>

            <div class="s20 items-center gap-6 pb-6 collapse-content-flex active">
                <div class="s3_mobile px-4">
                    <div class="rounded-100 overflow-hidden item active"><!--active-->
                        <img :src="!!user.photo_url ? user.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                    </div>
                    <h3 class="mt-5 text-center">{{ user.email || user.telephone || 'N/A' }}</h3>
                </div>
                <div class="s4_mobile">
                    <h3>{{ user.nom + ' ' + user.prenom }}</h3>
                    <h4 class="font-normal mt-2 mb-5">{{ user.collectivite?.nom || 'N/A' }}</h4>
                    <h6 class="font-semibold my-2">{{ user.telephone || 'N/A' }}</h6>
                    <h6 class="font-semibold my-2">{{ user.email || 'N/A' }}</h6>
                    <div class="clear-both h-10"></div>
                    <h6 class="font-normal mb-4" v-if="user.date_engagement">
                        Date d'engagement <br class="mb-2"/>
                        <b>{{ formatDateFrench(user.date_engagement) }} - {{ calculateAnciennete(user.date_engagement) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4" v-else>
                        Date d'engagement <br class="mb-2"/>
                        <b>-</b>
                    </h6>

                    <div class="clear-both h-20"></div>
                    <span v-if="!user.email_verified_at" class="tag-state warning stroke">En cours</span>
                    <span v-if="user.email_verified_at" class="tag-state success stroke">Vérifié</span>
                    <span v-if="user.actif == 1" class="tag-state notif stroke">Actif</span>
                    <span v-if="user.actif == 0" class="tag-state danger stroke">Inactif</span>
                    <div class="clear-both h-20"></div>
                    <p v-if="user.last_connection_at" class="flex gap-2 text-grey-200 my-3">
                        <img src="/icons/icon-calendrier.svg" class="size-xs" /> 
                        Dernière connexion: {{ formatDate(user.last_connection_at, 'DD/MM/YYYY') }}
                    </p>
                </div>
                <div>
                    <hr class="hr-vertical hr-vertical-white h-100p"/>
                </div>
                <div class="s4_mobile px-6">
                    <h5 class="font-normal mb-4" v-if="user.created_at">
                        Date de souscription <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(user.created_at) }}</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-else>
                        Date de souscription <br class="mb-2"/>
                        <b class="text-xl">-</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-if="user.souscription?.date_fin">
                        Date de fin de souscription <br class="mb-2"/>
                        <b class="text-xl">{{ formatDateFrench(user.souscription.date_fin) }}</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-else>
                        Date de fin de souscription <br class="mb-2"/>
                        <b class="text-xl">-</b>
                    </h5>
                    <hr class="hr-white"/>
                    <h5 class="font-normal mb-4">
                        Utilisation total <br class="mb-2"/>
                        <b class="text-xl">{{ user.balance ? formatAmount(user.balance) + ' F' : '0 F' }}</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-if="user.salaire">
                        Salaire Net après Utilisation <br class="mb-2"/>
                        <b class="text-xl">{{ formatAmount((user.salaire || 0) - (user.balance || 0)) }} F</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-else>
                        Salaire Net après Utilisation <br class="mb-2"/>
                        <b class="text-xl">-</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-if="user.quotite_cessible_pourcent && user.quotite_cessible_montant">
                        Quotité cessible disponible <br class="mb-2"/>
                        <b class="text-xl">{{ user.quotite_cessible_pourcent }}% - {{ formatAmount(user.quotite_cessible_montant) }} F</b>
                    </h5>
                    <h5 class="font-normal mb-4" v-else>
                        Quotité cessible disponible <br class="mb-2"/>
                        <b class="text-xl">-</b>
                    </h5>
                </div>

                <div class="s9_mobile">
                    <div class="grid2">
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">Net</h2>
                                <h4 class="secondary-font font-normal text-grey-100"></h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Salaire</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ user.salaire ? formatAmount(user.salaire) + ' F' : '-' }}</h2>
                            </div>
                        </div>
                        <!--CardQuotite - Component-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">{{ user.quotite_cessible_pourcent ? user.quotite_cessible_pourcent + '%' : '-' }}</h2>
                                <h4 class="secondary-font font-normal text-grey-100"></h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Prélèvement sur salaire</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ user.quotite_cessible_montant ? formatAmount(user.quotite_cessible_montant) + ' F' : '-' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <!--CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.8);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">Par</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Mon revolving</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ user.balance ? formatAmount(user.balance) + ' F' : '-' }}</h2>
                            </div>
                        </div>
                        <!--End CardQuotite-->
                        <div class="widget shadow-200 flex align-items-center rounded-30 px-7 py-5" style="background-color: rgba(var(--color-black-rgb), 0.5);">
                            <div class="s6">
                                <h2 class="secondary-font font-normal text-grey-100">{{ user.souscription?.duree_mois || '-' }}</h2>
                                <h4 class="secondary-font font-normal text-grey-100">mois</h4>
                            </div>
                            <div class="s14">
                                <h6 class="secondary-font font-medium text-grey-100 mb-1">Pouvoir d'achat</h6>
                                <h2 class="secondary-font font-bold text-primary-color">{{ user.souscription?.montant ? formatAmount(user.souscription.montant) + ' F' : '-' }}</h2>
                            </div>
                        </div>
                    </div>
                    <!----->
                    <div class="clear-both h-30"></div>
                    <!--Barre de progression - Component-->
                    <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-white-rgb), 0.1);">
                        <div class="widget-floating py-0 text-xl flex-right">Utilisation</div>
                        <div class="h-100p flex align-items-center justify-content-left rounded-100 text-2xl px-5 bg-pop-green" :style="`width: ${quotiteUsedPercent}%;`">
                            {{ quotiteUsedPercent }}%
                        </div>
                    </div>
                    <!--End Barre de progression-->
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>



        <!--Contrat d'engagement - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Identification / Bulettin
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn-ultra"><!--loading-->
                    <div class="btn-show">
                        <img src="/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
                        <span>Valider</span>
                    </div>
                    <div class="btn-loading">
                        <div class="loader-dots"></div>
                    </div>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
                <div class="s7_mobile pt-5">
                    <h3 class="font-normal text-primary-color mb-4">
                        Identification &nbsp; 
                        <span v-if="user.kyc?.statut === 'verified'" class="tag-state notif stroke">Validé</span>
                        <span v-else-if="user.kyc?.statut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    
                    <div class="s6 rounded-50 overflow-hidden item"><!--active-->
                        <img :src="!!user.photo_url ? user.photo_url : '/assets/users_avatar/600w/user.jpg'" width="100%" />
                    </div>
                    <div class="s14 mt-5 px-5">
                        <h4 class="text-grey-900 mb-3" v-if="user.kyc?.id">n° CF{{ user.created_at ? moment(user.created_at).format('YYYY') : '2025' }}-{{ String(user.kyc.id).padStart(6, '0') }}</h4>
                        <h4 class="text-grey-900 mb-3" v-else>n° -</h4>
                        <h5 class="font-normal my-1 text-grey-800" v-if="user.kyc?.created_at">Effectuée le</h5>
                        <h5 class="font-normal my-1 text-grey-800" v-if="user.kyc?.created_at">{{ formatDate(user.kyc.created_at, 'DD/MM/YYYY HH:mm') }}</h5>
                        <h5 class="font-normal my-1 text-grey-800" v-else-if="user.created_at">{{ formatDate(user.created_at, 'DD/MM/YYYY HH:mm') }}</h5>
                        <h5 class="font-normal my-1 text-grey-800" v-else>-</h5>
                        <h5 class="text-grey-900 mt-4">{{ user.nom + ' ' + user.prenom }}</h5>
                    </div>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s6_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Pièce d'identité &nbsp; 
                        <span v-if="user.kyc?.statut === 'verified'" class="tag-state notif stroke">Validé</span>
                        <span v-else-if="user.kyc?.statut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900 my-2" v-if="user.numero_piece_identite">
                        N° {{ user.numero_piece_identite }} / {{ user.type_piece_identite || 'CNI' }}
                    </h4>
                    <h4 class="text-grey-900 my-2" v-else-if="user.kyc?.piece_numero">
                        N° {{ user.kyc.piece_numero }} / {{ user.kyc?.piece_type || 'CNI' }}
                    </h4>
                    <h4 class="text-grey-900 my-2" v-else>
                        N° - / -
                    </h4>
                    <h6 class="font-normal mb-4 text-grey-800" v-if="user.kyc?.piece_expiration">
                        Date d'expiration 
                        <b>{{ formatDateFrench(user.kyc.piece_expiration) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-else>
                        Date d'expiration 
                        <b>-</b>
                    </h6>
                    <div class="flex gap-4" v-if="user.document_identite_recto || user.document_identite_verso">
                        <img v-if="user.document_identite_recto" :src="user.document_identite_recto.fichier_url" width="100%" />
                        <img v-if="user.document_identite_verso" :src="user.document_identite_verso.fichier_url" width="100%" />
                    </div>
                    <div class="flex gap-4" v-else-if="user.kyc && user.kyc.kyc_documents && user.kyc.kyc_documents.length > 0">
                        <img v-for="(doc, idx) in user.kyc.kyc_documents.slice(0, 2)" :key="idx" :src="doc.fichier_url" width="100%" />
                    </div>
                    <div class="flex gap-4" v-else>
                        <div class="text-grey-500">Aucun document disponible</div>
                    </div>
                    
                    <div class="clear-both h-10"></div>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s7_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Bulletins de salaires &nbsp; 
                        <span v-if="user.kyc?.statut === 'verified'" class="tag-state notif stroke">Validé</span>
                        <span v-else-if="user.kyc?.statut === 'rejected'" class="tag-state danger stroke">Rejeté</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>

                    <template v-if="user.bulletin1_document || user.bulletin2_document || user.bulletin3_document">
                        <a v-if="user.bulletin1_document" :href="user.bulletin1_document.fichier_url" target="_blank">
                            <h5 class="font-normal mb-2 text-grey-800">
                                Bulletin 1 - <span class="text-grey-500">{{ formatDateFrench(user.bulletin1_document.created_at) }}</span>
                            </h5>
                        <h5 class="mb-5 text-grey-800">
                            <img src="/icons/icon-download.svg" class="size-sm mr-2" alt="" />
                                {{ user.bulletin1_document.fichier || 'fichier_bulletin.pdf' }}
                        </h5>
                    </a>
                        <a v-if="user.bulletin2_document" :href="user.bulletin2_document.fichier_url" target="_blank">
                            <h5 class="font-normal mb-2 text-grey-800">
                                Bulletin 2 - <span class="text-grey-500">{{ formatDateFrench(user.bulletin2_document.created_at) }}</span>
                            </h5>
                        <h5 class="mb-5 text-grey-800">
                            <img src="/icons/icon-download.svg" class="size-sm mr-2" alt="" />
                                {{ user.bulletin2_document.fichier || 'fichier_bulletin.pdf' }}
                        </h5>
                    </a>
                        <a v-if="user.bulletin3_document" :href="user.bulletin3_document.fichier_url" target="_blank">
                            <h5 class="font-normal mb-2 text-grey-800">
                                Bulletin 3 - <span class="text-grey-500">{{ formatDateFrench(user.bulletin3_document.created_at) }}</span>
                            </h5>
                        <h5 class="mb-5 text-grey-800">
                            <img src="/icons/icon-download.svg" class="size-sm mr-2" alt="" />
                                {{ user.bulletin3_document.fichier || 'fichier_bulletin.pdf' }}
                        </h5>
                    </a>
                    </template>
                    <template v-else>
                        <p class="text-grey-500">Aucun bulletin disponible</p>
                    </template>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>



        <!--Contrat d'engagement - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Contrat d'engagement
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn-ultra"><!--loading-->
                    <div class="btn-show">
                        <img src="/icons/icon-valider.svg" class="btn-icon icon-neutral" alt="" />
                        <span>Valider</span>
                    </div>
                    <div class="btn-loading">
                        <div class="loader-dots"></div>
                    </div>
                </button>
            </div>
            <div class="clear-both h-5"></div>

            <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
                <div class="s7_mobile pt-5">
                    <h3 class="font-normal text-primary-color mb-4">
                        Coop Finance &nbsp; 
                        <span v-if="user.souscription_id" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900" v-if="user.souscription_id">
                        Contrat n° / CF{{ user.created_at ? moment(user.created_at).format('YYYY') : '2025' }}-{{ String(user.souscription_id).padStart(6, '0') }}
                    </h4>
                    <h4 class="text-grey-900" v-else>Contrat n° / -</h4>
                    <h5 class="font-normal my-2 text-grey-800" v-if="user.souscription?.created_at">
                        Signé le {{ formatDateFrench(user.souscription.created_at) }}
                    </h5>
                    <h5 class="font-normal my-2 text-grey-800" v-else>Signé le -</h5>
                    <h6 class="font-semibold my-2 text-grey-800" v-if="user.date_engagement">
                        Durée: {{ calculateAnciennete(user.date_engagement) }}
                    </h6>
                    <h6 class="font-semibold my-2 text-grey-800" v-else>Durée: -</h6>
                    <div class="clear-both h-10"></div>
                    <h4 class="font-normal mb-2 text-grey-800">Montant du contrat</h4>
                    <h4 class="mb-2 text-grey-800" v-if="user.souscription?.montant">{{ formatAmount(user.souscription.montant) }} F</h4>
                    <h4 class="mb-2 text-grey-800" v-else>-</h4>

                    <div class="clear-both h-20"></div>
                    <span v-if="!user.email_verified_at" class="tag-state warning stroke">En cours</span>
                    <span v-if="user.email_verified_at" class="tag-state success stroke">Vérifié</span>
                    <span v-if="user.actif == 1" class="tag-state notif stroke">Actif</span>
                    <span v-if="user.actif == 0" class="tag-state danger stroke">Inactif</span>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s7_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        {{ user.collectivite?.nom || 'Collectivité' }} &nbsp; 
                        <span v-if="user.collectivite_id" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900 my-2">Identifiant / {{ user.email || user.telephone || 'N/A' }}</h4>
                    <h6 class="font-normal mb-4 text-grey-800" v-if="user.date_engagement">
                        Date de début de contrat <br class="mb-2"/>
                        <b>{{ formatDateFrench(user.date_engagement) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-else>
                        Date de début de contrat <br class="mb-2"/>
                        <b>-</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-if="user.souscription?.date_fin">
                        Date de fin de contrat <br class="mb-2"/>
                        <b>{{ formatDateFrench(user.souscription.date_fin) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-else>
                        Date de fin de contrat <br class="mb-2"/>
                        <b>-</b>
                    </h6>
                    <div class="clear-both h-10"></div>
                    <h4 class="font-normal mb-2 text-grey-800">Fond de la collectivité</h4>
                    <h4 class="mb-2 text-grey-800">-</h4>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s6_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Membre &nbsp; 
                        <span v-if="user.email_verified_at && user.actif == 1" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900 my-2">Identifiant / {{ user.email || user.telephone || 'N/A' }}</h4>
                    <h4 class="text-grey-900 my-2">{{ user.nom + ' ' + user.prenom }}</h4>
                    <h6 class="font-normal mb-4 text-grey-800" v-if="user.date_engagement">
                        Date de début de contrat <br class="mb-2"/>
                        <b>{{ formatDateFrench(user.date_engagement) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-else>
                        Date de début de contrat <br class="mb-2"/>
                        <b>-</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-if="user.souscription?.date_fin">
                        Date de fin de contrat <br class="mb-2"/>
                        <b>{{ formatDateFrench(user.souscription.date_fin) }}</b>
                    </h6>
                    <h6 class="font-normal mb-4 text-grey-800" v-else>
                        Date de fin de contrat <br class="mb-2"/>
                        <b>-</b>
                    </h6>
                    <div class="clear-both h-10"></div>
                    <h4 class="font-normal mb-2 text-grey-800">Prélèvement mensuel</h4>
                    <h4 class="mb-2 text-grey-800" v-if="user.quotite_cessible_montant">{{ formatAmount(user.quotite_cessible_montant) }} F</h4>
                    <h4 class="mb-2 text-grey-800" v-else>-</h4>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Premier prélèvement / Carte - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open-flex active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Premier prélèvement / Carte 
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn lg py-3 white2 active">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Carte</span>
                </button>
            </div>
            <div class="clear-both h-0"></div>

            <div class="s20 items-center gap-6 px-5 pb-6 collapse-content-flex active">
                <div class="s7_mobile pt-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Premier prélèvement &nbsp; 
                        <span v-if="user.souscription_id" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900" v-if="user.souscription_id">
                        Signature / CF{{ user.created_at ? moment(user.created_at).format('YYYY') : '2025' }}-{{ String(user.souscription_id).padStart(6, '0') }}
                    </h4>
                    <h4 class="text-grey-900" v-else>Signature / -</h4>
                    <h5 class="font-normal my-2 text-grey-800" v-if="user.souscription?.created_at">
                        Effectué le {{ formatDate(user.souscription.created_at, 'DD/MM/YYYY HH:mm') }}
                    </h5>
                    <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
                    <div class="clear-both h-10"></div>
                    <h4 class="font-normal mb-2 text-grey-800">Montant du prélèvement</h4>
                    <h4 class="mb-2 text-grey-800" v-if="user.quotite_cessible_montant">{{ formatAmount(user.quotite_cessible_montant) }} F</h4>
                    <h4 class="mb-2 text-grey-800" v-else>-</h4>

                    <div class="clear-both h-20"></div>
                    <span v-if="!user.email_verified_at" class="tag-state warning stroke">En cours</span>
                    <span v-if="user.email_verified_at" class="tag-state success stroke">Vérifié</span>
                    <span v-if="user.actif == 1" class="tag-state notif stroke">Actif</span>
                    <span v-if="user.actif == 0" class="tag-state danger stroke">Inactif</span>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s7_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Création de la carte &nbsp; 
                        <span v-if="user.cartes && user.cartes.length > 0" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h4 class="text-grey-900 my-2" v-if="user.cartes && user.cartes.length > 0">
                        **** **** **** {{ user.cartes[0].numero_carte ? user.cartes[0].numero_carte.slice(-4) : '****' }}
                    </h4>
                    <h4 class="text-grey-900 my-2" v-else>**** **** **** ****</h4>
                    <h4 class="text-grey-900 my-2">{{ user.collectivite?.nom || 'N/A' }}</h4>
                    <h5 class="font-normal my-2 text-grey-800" v-if="user.cartes && user.cartes[0]?.created_at">
                        Créé le {{ formatDate(user.cartes[0].created_at, 'DD/MM/YYYY HH:mm') }}
                    </h5>
                    <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
                    <div class="clear-both h-10"></div>
                    <h4 class="text-grey-800" v-if="user.cartes && user.cartes[0]?.rib">RIB / {{ user.cartes[0].rib }}</h4>
                    <h4 class="text-grey-800" v-else>RIB / -</h4>
                    <div class="clear-both h-10"></div>
                    <h4 class="font-normal mb-2 text-grey-800">Montant disponible</h4>
                    <h4 class="mb-2 text-grey-800" v-if="user.balance">{{ formatAmount(user.balance) }} F</h4>
                    <h4 class="mb-2 text-grey-800" v-else>-</h4>
                </div>
                <div class="flex-center">
                    <hr class="hr-vertical h-100p"/>
                </div>

                <div class="s6_mobile px-6">
                    <h3 class="font-normal text-primary-color mb-4">
                        Livraison &nbsp; 
                        <span v-if="user.cartes && user.cartes[0]?.date_livraison" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h5 class="text-grey-900 my-2">Point relais - collectivité</h5>
                    <h5 class="font-normal my-2 text-grey-800" v-if="user.cartes && user.cartes[0]?.date_livraison">
                        Livré le {{ formatDate(user.cartes[0].date_livraison, 'DD/MM/YYYY HH:mm') }}
                    </h5>
                    <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
                    <div class="clear-both h-20"></div>
                    <h3 class="font-normal text-primary-color mb-4">
                        Réception &nbsp; 
                        <span v-if="user.cartes && user.cartes[0]?.date_reception" class="tag-state notif stroke">Validé</span>
                        <span v-else class="tag-state warning stroke">En attente</span>
                    </h3>
                    <h5 class="text-grey-900 my-2">Point relais - collectivité</h5>
                    <h5 class="font-normal my-2 text-grey-800" v-if="user.cartes && user.cartes[0]?.date_reception">
                        Reçu le {{ formatDate(user.cartes[0].date_reception, 'DD/MM/YYYY HH:mm') }}
                    </h5>
                    <h5 class="font-normal my-2 text-grey-800" v-else>-</h5>
                </div>
            </div>
        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Prélèvement mensuel - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Prélèvement mensuel
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn lg py-3 white2 active">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Plan mensuel</span>
                </button>
                <!-- <button class="btn lg py-3 black active">
                    <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                    <span>Exporter en PDF</span>
                </button>
                <button class="btn lg py-3 black active">
                    <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                    <span>Exporter en Excel</span>
                </button> -->
            </div>

            <div class="s20 items-center gap-6 pb-4 collapse-content active">
                <div class="clear-both h-20"></div>
                <!--Barre de progression - Component-->
                <div class="h-60 rounded-100 position-relative" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="widget-floating py-0 text-xl flex-right">{{ quotiteUsedPercent }}%</div>
                            <div class="h-100p flex align-items-center rounded-100 px-7 bg-pop-green" :style="`width: ${quotiteUsedPercent}%;`">
                                <h2>{{ user.balance ? formatAmount(user.balance) + ' F' : '0 F' }}</h2><h2 class="font-medium"> &nbsp;/ {{ user.quotite_cessible_montant ? formatAmount(user.quotite_cessible_montant) + ' F' : (user.salaire ? formatAmount(user.salaire) + ' F' : '-') }}</h2>
                    </div>
                </div>
                <!--End Barre de progression-->

                <div class="clear-both h-30"></div>

                <div class="grid4 px-1">
                    <!--Prélèvement - Component-->
                    <div class="widget border border-3 active shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">250 000 F</h3>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-grey-600 pb-2 display-none">1m 29j 24min</h5>
                            <div class="mb-4">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4">Le 11 Déc. 2025</h6>
                            <button class="btn-ultra w-100 shadow-0 display-none">
                                <div class="btn-show">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </div>
                                <div class="btn-loading">
                                    <div class="loader-dots"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                    <!--End Prélèvement-->

                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">250 000 F</h3>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-grey-600 pb-2">1m 29j 24min</h5>
                            <div class="mb-4  display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4 display-none">Le 11 Déc. 2025</h6>
                            <button class="btn-ultra w-100 shadow-0">
                                <div class="btn-show">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </div>
                                <div class="btn-loading">
                                    <div class="loader-dots"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">250 000 F</h3>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-grey-600 pb-2">1m 29j 24min</h5>
                            <div class="mb-4  display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4 display-none">Le 11 Déc. 2025</h6>
                            <button class="btn-ultra w-100 shadow-0 display-none">
                                <div class="btn-show">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </div>
                                <div class="btn-loading">
                                    <div class="loader-dots"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">250 000 F</h3>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-grey-600 pb-2">1m 29j 24min</h5>
                            <div class="mb-4  display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <h6 class="secondary-font font-semibold text-grey-600 pt-1 mb-4 display-none">Le 11 Déc. 2025</h6>
                            <button class="btn-ultra w-100 shadow-0 display-none">
                                <div class="btn-show">
                                    <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                    <span>Valider</span>
                                </div>
                                <div class="btn-loading">
                                    <div class="loader-dots"></div>
                                </div>
                            </button>
                        </div>
                    </div>

                </div>
                
                <div class="collapse-content">
                    <div class="grid4 px-1">
                        Plus
                    </div>
                </div>
                <div class="clear-both h-15"></div>
                <span class="px-5 py-4 bg-color rounded-100 font-bold text-lg collapse-open align-right">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Voir tous
                </span>
            </div>

        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>


        <!--Décaissement mensuel - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Décaissement mensuel
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right gap-4">
                <button class="btn lg py-3 white2 active">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Revolving</span>
                </button>
                <!-- <button class="btn lg py-3 black active">
                    <img src="/icons/icon-pdf.svg" class="btn-icon" alt="" />
                    <span>Exporter en PDF</span>
                </button>
                <button class="btn lg py-3 black active">
                    <img src="/icons/icon-excel.svg" class="btn-icon" alt="" />
                    <span>Exporter en Excel</span>
                </button> -->
            </div>

            <div class="s20 items-center gap-6 px-5 pb-4 collapse-content active">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Débloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">184 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">+4.6%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" style="width: 4.6%;">
                                4.6%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-purple"></div>
                            Bloqué
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">184 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">95.4%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" style="width: 95.4%;">
                                95.4%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Total
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2">4 000 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                </div>

                <div class="clear-both h-30"></div>

                <div class="grid4 px-1">
                    <!--Décaissement - Component-->
                    <div class="widget border border-3 active shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">378 830 F</h3>
                            <p class="secondary-font font-semibold text-purple">1.5%</p>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-primary-color pb-1 display-none">1m 29j 24min</h5>
                            <div class="mb-4">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <p class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
                            <h6 class="secondary-font font-semibold text-grey-600 py-1">11 Déc. 2025</h6>
                        </div>
                        <div class="s20 mt-2 display-none">
                            <p class="s10 font-medium text-sm text-grey-600 mt-2">
                                Prélèvement <span class="text-sm color-success">(Effectué)</span>
                            </p>
                            <div class="s10">
                                <button class="btn-ultra w-100">
                                    <div class="btn-show">
                                        <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                        <span>Décaisser</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    <!--End Décaissement-->

                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">378 830 F</h3>
                            <p class="secondary-font font-semibold text-purple">1.5%</p>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-primary-color pb-1">1m 29j 24min</h5>
                            <div class="mb-4 display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <p class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
                            <h6 class="secondary-font font-semibold text-grey-600 py-1">11 Déc. 2025</h6>
                        </div>
                        <div class="s20 mt-2">
                            <p class="s10 font-medium text-sm text-grey-600 mt-2">
                                Prélèvement <span class="text-sm color-success">(Effectué)</span>
                            </p>
                            <div class="s10">
                                <button class="btn-ultra w-100">
                                    <div class="btn-show">
                                        <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                        <span>Décaisser</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5 opacity-6">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">378 830 F</h3>
                            <p class="secondary-font font-semibold text-purple">1.5%</p>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-primary-color pb-1">1m 29j 24min</h5>
                            <div class="mb-4 display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <p class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
                            <h6 class="secondary-font font-semibold text-grey-600 py-1">11 Déc. 2025</h6>
                        </div>
                        <div class="s20 mt-2 display-none">
                            <p class="s10 font-medium text-sm text-grey-600 mt-2">
                                Prélèvement <span class="text-sm color-success">(Effectué)</span>
                            </p>
                            <div class="s10">
                                <button class="btn-ultra w-100">
                                    <div class="btn-show">
                                        <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                        <span>Décaisser</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>
                    <div class="widget border border-3 shadow-200 rounded-30 gap-5 px-7 py-5 opacity-6">
                        <div class="s10">
                            <h6 class="secondary-font font-semibold mb-1">01 Novembre 2025</h6>
                            <h3 class="secondary-font font-semibold text-grey-900 py-1">378 830 F</h3>
                            <p class="secondary-font font-semibold text-purple">1.5%</p>
                        </div>
                        <div class="s10 text-right">
                            <h5 class="secondary-font font-semibold text-primary-color pb-1">1m 29j 24min</h5>
                            <div class="mb-4 display-none">
                                <span class="tag-state notif stroke">Effectué</span>
                            </div>
                            <p class="secondary-font font-semibold text-grey-600 pt-1">Date de déblocage</p>
                            <h6 class="secondary-font font-semibold text-grey-600 py-1">11 Déc. 2025</h6>
                        </div>
                        <div class="s20 mt-2 display-none">
                            <p class="s10 font-medium text-sm text-grey-600 mt-2">
                                Prélèvement <span class="text-sm color-success">(Effectué)</span>
                            </p>
                            <div class="s10">
                                <button class="btn-ultra w-100">
                                    <div class="btn-show">
                                        <img src="/icons/icon-valider.svg" class="btn-icon" alt="" />
                                        <span>Décaisser</span>
                                    </div>
                                    <div class="btn-loading">
                                        <div class="loader-dots"></div>
                                    </div>
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
                <div class="collapse-content">
                    <div class="grid4 px-1">
                        Plus
                    </div>
                </div>
                <div class="clear-both h-15"></div>
                <span class="px-5 py-4 bg-color rounded-100 font-bold text-lg collapse-open align-right">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Voir tous
                </span>
            </div>

        </div>

        <div class="clear-both h-5"></div>
        <div class="step vertical bg-grey-500 ml-auto mr-auto"></div>
        <div class="clear-both h-5"></div>

        
        <!--Wallet / Utilisation - Component-->
        <div class="s20 widget px-6 pt-8 pb-2">
            <div class="s10 mt--4 ml--4 mb-5 collapse-open active">
                <span class="px-5 py-4 bg-color-black rounded-100 text-xl">
                    <img src="/icons/icon-panel-open.svg" class="size-xs icon-neutral mr-2 collapse-icon-open" alt="" />
                    <img src="/icons/icon-panel-close.svg" class="size-xs icon-neutral mr-2 collapse-icon-close" alt="" />
                    Wallet / Utilisation
                </span>
            </div>
            <div class="s10 mt--5-5 flex-right">
                <button class="btn lg py-3 white2 active">
                    <img src="/icons/icon-consulter.svg" class="btn-icon" alt="" />
                    <span>Utilisation</span>
                </button>
            </div>

            <div class="s20 gap-6 px-5 pb-8 collapse-content active">
                <div class="clear-both h-10"></div>
                <div class="flex items-center gap-6">
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-color-1000"></div>
                            Solde
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">184 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">92%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-color-1000 text-white" style="width: 92%;">
                                92%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s7">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-purple"></div>
                            Montant Utilisé
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">52 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">56.4%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-purple text-white" style="width: 56.4%;">
                                56.4%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                    <div class="s6">
                        <h5 class="flex gap-3 secondary-font font-semibold text-grey-700">
                            <div class="circle-medium bg-green-light"></div>
                            Dernier décaissement
                        </h5>
                        <h1 class="secondary-font font-semibold text-grey-900 my-2 text-5xl">184 350 F</h1>
                        <h5 class="secondary-font font-semibold text-grey-700 mb-4">100%</h5>
                        <!--Barre de progression - Component-->
                        <div class="h-40 rounded-100 position-relative overflow-hidden" style="width: 100%; background-color: rgba(var(--color-black-rgb), 0.1);">
                            <div class="h-100p flex align-items-center justify-content-left rounded-100 text-xl px-5 bg-green-light" style="width: 100%;">
                                100%
                            </div>
                        </div>
                        <!--End Barre de progression-->
                    </div>
                </div>
                
            </div>

        </div>
        <div class="clear-both h-80"></div>

    </section>
</template>